import { toast } from "react-toastify";
import { deleteItems, updateStateData } from "../Store/actions";
import store from "../Store/store";
import {
  allNumbersBetweenRange,
  allNumbersBetweenRangeHoursWrapAround,
  allWeekDayOptions,
  checkStatus,
  checkTodayDate,
  dateInCorrectTimezone,
  dbFormatDate,
  formatToStandardDate,
  isValidFileName,
  standardFormatToday,
  weekDayNames,
  weekDayNum,
} from "./utils";
import platformConfig from "../platformConfig";
import { createCookie, deleteCookie, sendApiRequest, sendApiRequestWithFile, setBatchLoadingStatus } from "./requestConfig";
import { LISTING_RELOAD, withListingReload } from "./listingReload";
import { checkUserDetailsFromCookie } from "./userManagement";
import { ownerCompanyIdForSave, ownerRequiredOnCreate, shouldSendSelectedOwner } from "./crossCompanyAccess";
import {
  clearStagedOwnerOutsideRetailerNotice,
  showStagedOwnerOutsideRetailerNotice,
  stageOwnerOutsideRetailerNotice,
} from "../components/commonComponents/Hoc/accessControlToasts";
import { rootRetailerOptionFields } from "../components/commonComponents/Filters/storeFilterUtils";
import moment from "moment";

// Dropdown options keep id, label, and retailer_id. Root retailer is added beside those
// so the grouped store dropdown can use it without changing save or the detail modal.
const buildVisualStoreOptions = (storesList = []) => {
  return (Array.isArray(storesList) ? storesList : []).map((storeRecord) => {
    const option = {
      label: storeRecord.name,
      id: storeRecord.id,
      retailer_id: storeRecord.retailer_id,
    };
    const rootRetailer = storeRecord?.root_retailer_company;
    if (
      typeof rootRetailer?.id !== "undefined" &&
      rootRetailer?.id !== null &&
      typeof rootRetailer?.name === "string" &&
      rootRetailer.name !== ""
    ) {
      option.root_retailer_company_id = rootRetailer.id;
      option.root_retailer_company_name = rootRetailer.name;
    }
    return option;
  });
};

const saveStoresForVisuals = (storesList) => {
  const storeOptions = buildVisualStoreOptions(storesList);
  store.dispatch(
    updateStateData({
      stores_list_for_visuals: storesList,
      // Slim dropdown options for the visual stores field.
      restructured_stores_for_visuals: storeOptions,
      // New visual starts with every store selected, same as the old region filter.
      selected_store_options: storeOptions,
    })
  );
};


// manage pagination cookie infomation
export const manageClipsPaginationCookie = (campaignPaginationObj) => {
  const currentStateData = store.getState().userSelections;
  //
  const paginationObj = typeof currentStateData.clips_pagination !== "undefined" ? currentStateData.clips_pagination : {};

  store.dispatch(updateStateData({ clips_pagination: { ...paginationObj, ...campaignPaginationObj } }));
  deleteCookie("clips_pagination");
  // set cookie for pagination
  createCookie("clips_pagination", JSON.stringify({ ...paginationObj, ...campaignPaginationObj }));
}

// clips listing
export const loadClips = async (pageNumber, pageSize) => {
  return withListingReload(LISTING_RELOAD.clips, () => loadClipsPage(pageNumber, pageSize));
};

const loadClipsPage = async (pageNumber, pageSize) => {
  const currentStateData = store.getState().userSelections;
  // check if page number and page size are passed from the component, if not then set default values.
  if (typeof pageNumber === "undefined" && typeof pageSize === "undefined") {
    pageNumber = 0;
    pageSize = 10;
    // page number/size from the cookies if available
    if (typeof currentStateData.clips_pagination !== "undefined") {
      pageNumber = currentStateData.clips_pagination.pageNumber;
      pageSize = currentStateData.clips_pagination.pageSize;
    }
  }
  let clipsListApiEndpoint = "clips?page=" + pageNumber + "&size=" + pageSize;
  // check if filters are applied, then we need to fetch the filtered list.
  if (
    typeof currentStateData.clip_list_filters !== "undefined" && currentStateData.clips_list_filter_applied === true
  ) {
    // campaign id/name filter
    if (
      typeof currentStateData.clip_list_filters.campaign_name !== "undefined" &&
      currentStateData.clip_list_filters.campaign_name.length !== 0
    ) {
      currentStateData.clip_list_filters.campaign_name.forEach((item) => {
        clipsListApiEndpoint = clipsListApiEndpoint + "&campaign_id=" + item;
      });
    }
    // clip name filter
    if (
      typeof currentStateData.clip_list_filters.clip_name !== "undefined" &&
      currentStateData.clip_list_filters.clip_name.length !== 0
    ) {
      // duplicate visual names collapse to one dropdown option, so clip_name_ids holds
      // every id sharing a chosen name; fall back to clip_name for filters saved before it existed
      const clipNameIds =
        Array.isArray(currentStateData.clip_list_filters.clip_name_ids) &&
        currentStateData.clip_list_filters.clip_name_ids.length !== 0
          ? currentStateData.clip_list_filters.clip_name_ids
          : currentStateData.clip_list_filters.clip_name;
      clipNameIds.forEach((item) => {
        clipsListApiEndpoint = clipsListApiEndpoint + "&clip_id=" + item;
      });
    }
    // retailer/owner filter
    if (
      typeof currentStateData.clip_list_filters.clip_company_name !== "undefined" &&
      currentStateData.clip_list_filters.clip_company_name.length !== 0
    ) {
      currentStateData.clip_list_filters.clip_company_name.forEach((item) => {
        clipsListApiEndpoint = clipsListApiEndpoint + "&id_company=" + item;
      });
    }
    // Root retailer filter (UI label "Retailer"; Redux key root_retailer_filter)
    if (
      typeof currentStateData.clip_list_filters.root_retailer_filter !== "undefined" &&
      currentStateData.clip_list_filters.root_retailer_filter.length !== 0
    ) {
      currentStateData.clip_list_filters.root_retailer_filter.forEach((item) => {
        clipsListApiEndpoint = clipsListApiEndpoint + "&retailer=" + item;
      });
    }
    // progress filter
    if (
      typeof currentStateData.clip_list_filters.status_filter !== "undefined" &&
      currentStateData.clip_list_filters.status_filter.length !== 0
    ) {
      currentStateData.clip_list_filters.status_filter.forEach((item) => {
        clipsListApiEndpoint = clipsListApiEndpoint + "&progress=" + item;
      });
    }
    // store filter
    if (
      typeof currentStateData.clip_list_filters.clip_store_filter !== "undefined" &&
      currentStateData.clip_list_filters.clip_store_filter.length !== 0
    ) {
      currentStateData.clip_list_filters.clip_store_filter.forEach((item) => {
        clipsListApiEndpoint = clipsListApiEndpoint + "&id_store=" + item;
      });
    }

    if (
      typeof currentStateData.clip_list_filters.clip_visual_type !== "undefined" &&
      currentStateData.clip_list_filters.clip_visual_type !== null
    ) {
      clipsListApiEndpoint = clipsListApiEndpoint + "&type=" + currentStateData.clip_list_filters.clip_visual_type;
    }

  }
  const checkClips = await sendApiRequest(clipsListApiEndpoint, "", "GET");

  let response = {};

  if (checkClips.success === true) {
    let clipsList = [];
    let clipNamesList = [];
    let clipStoresList = [];
    let alreadyUsedStores = [];
    let clipCompanyNamesList = [];

    /*need to restructure clips response as there's a lot of extra info returned in the response.*/
    if (checkClips.response.length !== 0) {
      //   loop through the clips response
      checkClips.response.content.forEach((clips) => {
        //decide campaign status by comparing current date with start and end date of campaign.
        let clipStatus = checkStatus(clips.date_start, clips.date_end);

        clipsList.push({
          id: clips.id,
          name: clips.content,
          campaign: clips.campaign.name,
          campaign_id: clips.campaign.id,
          campaign_start_date: clips.campaign.date_start,
          campaign_end_date: clips.campaign.date_end,
          campaign_finished: moment(clips.campaign.date_end).isBefore(moment(), "day") ? true : false,
          stores: clips.count_stores,
          start_date: clips.date_start,
          end_date: clips.date_end,
          campaign_contacts: clips.contacts,
          unlimited: clips.unlimited,
          last_edit_by: clips.last_edit,
          clip_volume: clips.volume,
          clip_unlimited: clips.unlimited,
          selected_weeks: clips.weeks,
          status: clipStatus,
          clip_company_name: clips.company.name,
          clip_company_id: clips.company.id,
          root_retailer_company: clips.root_retailer_company,
          // Retailer column shows every retailer of the clip (plain names, no objects)
          retailer_names: Array.isArray(clips.retailer_names) ? clips.retailer_names : [],
          active: clips.active,
          hours: clips.hours,
          selected_days: clips.weekdays.map((day) => {
            return weekDayNames[day];
          }),
          selected_store_options: clips?.stores?.map((storeInfo) => {
            return {
              label: storeInfo.name,
              id: storeInfo.id,
              ...rootRetailerOptionFields(storeInfo),
            };
          }),
        });
        clipNamesList.push({ id: clips.id, label: clips.content });
        clipStoresList = [
          ...clipStoresList,
          ...clips.stores
            .map((storeInfo) => {
              if (alreadyUsedStores.includes(storeInfo.name)) {
                return false;
              } else {
                alreadyUsedStores.push(storeInfo.name);
                return { label: storeInfo.name, id: storeInfo.id };
              }
            })
            .filter((store) => {
              return store !== false;
            }),
        ];
        clipCompanyNamesList.push({ id: clips.company.id, label: clips.company.name });
      });
    }
    // save to the redux state
    store.dispatch(
      updateStateData({
        clips_list: clipsList,
        // clipsList.sort((next, prev) => {
        //   return formatToStandardDate(prev.start_date) - formatToStandardDate(next.start_date);
        // }),
        clip_names_list: clipNamesList,
        clip_company_name_list: clipCompanyNamesList,
        clip_stores_list_filter: clipStoresList,
      })
    );

    // save/update campaign pagination info
    manageClipsPaginationCookie({
      totalPages: checkClips.response.totalPages,
      totalRecords: checkClips.response.totalElements,
      pageNumber: checkClips.response.number,
      pageSize: checkClips.response.size,
    })

    // scroll to the top of page
    window.scrollTo(0, 0);

    response = {
      success: true,
    };
  } else {
    response = {
      success: false,
    };
  }
  // currentStateData.loading = false;
  return response;
};

// fetch campaign names for filters/dropdowns etc.
export const fetchClipNames = async () => {
  const clipsList = await sendApiRequest("clips/list", {}, "GET");
  if (typeof clipsList.success !== "undefined" && clipsList.success === true) {
    let clipNamesList = [];
    let clipFullObj = [];
    clipsList.response.forEach((clips) => {
      clipNamesList.push({ id: clips.id, label: clips.content });
      clipFullObj.push(clips);
    });

    store.dispatch(
      updateStateData({
        clip_names_info: {
          clip_names_list: clipNamesList,
          clip_full_obj: clipFullObj,
        },
      })
    );

    return {
      success: true,
    };
  } else {
    return {
      success: false,
    };
  }
};

//to match campaign list records with the filters applied.
const matchClipRecords = (item) => {
  const currentStateData = store.getState().userSelections;
  const appliedFilters = currentStateData.clip_list_filters;

  let itemMatch = true;

  //campaign name filter
  if (appliedFilters.campaign_name.length !== 0) {
    if (!appliedFilters.campaign_name.some(filterWord => item.campaign_id === filterWord.id)) {
      return false;
    }
  }
  // clip name filter
  if (appliedFilters.clip_name.length !== 0) {
    if (!appliedFilters.clip_name.some(filterWord => item.name === filterWord.label)) {
      return false;
    }
  }

  // company name filter
  if (appliedFilters.clip_company_name.length !== 0) {
    if (!appliedFilters.clip_company_name.some(filterWord => item.clip_company_name === filterWord.label)) {
      return false;
    }
  }

  // clip store filter
  if (appliedFilters.clip_store_filter.length !== 0) {
    if (!appliedFilters.clip_store_filter.some(filterWord =>
      item.selected_store_options.some(store => store.label === filterWord.label)
    )) {
      return false;
    }
  }

  // when duration/progress filter is applied
  if (appliedFilters.status_filter.length !== 0) {
    if (!appliedFilters.status_filter.some(filterWord => (item.active === true && item.status === filterWord.label) ||
      (item.active === false && filterWord.label === "Stopped"))) {
      return false;
    }
  }
  return itemMatch;
};

export const filterClips = () => {
  const currentStateData = store.getState().userSelections;
  let filteredData = [];

  //filter live campaigns
  if (typeof currentStateData.clips_list !== "undefined" && currentStateData.clips_list.length !== 0) {
    currentStateData.clips_list.forEach((item) => {
      const checkMatch = matchClipRecords(item);
      if (checkMatch === true) {
        filteredData.push(item);
      }
    });
  }

  store.dispatch(
    updateStateData({
      filtered_clips_list: {
        clips_list: filteredData,
      },
      clips_list_filter_applied: true,
    })
  );

  return true;
};

//helper function for automaticly selected weekdays
function getWeekdaysInRange(startDate, endDate) {
  if (!startDate || !endDate) return [];

  startDate = formatToStandardDate(startDate);
  endDate = formatToStandardDate(endDate);

  const daysInSelectedTimeperiod = Math.round((endDate - startDate) / (1000 * 3600 * 24) + 1);

  // Check if the selected period is atleast than 7 days
  if (daysInSelectedTimeperiod <= 7) {
    const date = new Date(startDate.getTime());
    const weekDayNumbersInRange = [];
    const weekDayNamesAccordingToNumbers = [];

    while (date <= endDate) {
      weekDayNumbersInRange.push(date.getDay()); //JS standard 0 = Sunnday has to be converted
      date.setDate(date.getDate() + 1);
    }

    const weekDayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    weekDayNumbersInRange.forEach((day) => {
      weekDayNamesAccordingToNumbers.push(weekDayNames[day]);
    });

    // Filter allWeekDayOptions to only include days in the date range
    return allWeekDayOptions.filter((option) =>
      weekDayNamesAccordingToNumbers.includes(option.label)
    );
  } else {
    return allWeekDayOptions;
  }
}

// validate clips info
export const validateClipsInfo = (validateFor, selectedFiles) => {
  const currentStateData = store.getState().userSelections;
  let error = false;
  let errorMessages = {};
  // clip file
  // const clipFile =
  //   document.getElementById("uploadClipFile") !== null &&
  //     typeof document.getElementById("uploadClipFile").files !== "undefined" &&
  //     document.getElementById("uploadClipFile").files.length !== 0
  //     ? document.getElementById("uploadClipFile").files[0]
  //     : "";

  const clipFile =
    typeof selectedFiles !== "undefined" && selectedFiles.length !== 0
      ? selectedFiles[0]
      : "";

  // campaign id
  const campaign =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.campaign_id !== "undefined"
      ? currentStateData.new_clip_data.campaign_id
      : "";
  // start date
  const clipStartDate =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.clip_start_date !== "undefined"
      ? currentStateData.new_clip_data.clip_start_date
      : "";
  // end date
  const clipEndDate =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.clip_end_date !== "undefined"
      ? currentStateData.new_clip_data.clip_end_date
      : "";
  // weeks number (it will be an array)
  let clipWeeks =
    currentStateData.new_clip_data?.clip_weeks || [];
  // week days (it will also be an array)
  let clipWeekDays = currentStateData.new_clip_data?.clip_week_days || [];
  // time slots
  const slot_oneStart =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.slot_1_start_time !== "undefined"
      ? currentStateData.new_clip_data.slot_1_start_time
      : 0;
  const slot_oneEnd =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.slot_1_end_time !== "undefined"
      ? currentStateData.new_clip_data.slot_1_end_time
      : 23;
  // hours of day
  let clipHoursOfDay =
    currentStateData.new_clip_data?.hours || [];
  // contacts
  const clipContacts =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.clip_contacts !== "undefined"
      ? currentStateData.new_clip_data.clip_contacts
      : "";
  const clipVolume =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.clip_volume !== "undefined"
      ? currentStateData.new_clip_data.clip_volume
      : "";
  // unlimited option
  const clipUnlimited =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.clip_unlimited !== "undefined"
      ? currentStateData.new_clip_data.clip_unlimited
      : false;
  // optimized option
  const clipOptimized =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.clip_optimized !== "undefined"
      ? currentStateData.new_clip_data.clip_optimized
      : false
  // stores: selected_store_options is what the store dropdown shows, clip_stores only
  // mirrors it and stays empty while the preselected default was never touched
  const selectedStoreOptions =
    typeof currentStateData.selected_store_options !== "undefined" ? currentStateData.selected_store_options : [];
  const clipStores = selectedStoreOptions.length !== 0
    ? selectedStoreOptions.map((storeOption) => storeOption.label)
    : typeof currentStateData.clip_stores !== "undefined" ? currentStateData.clip_stores : [];

  const playoutStatus = typeof currentStateData.new_clip_data !== "undefined" &&
    typeof currentStateData.new_clip_data.active !== "undefined"
    ? currentStateData.new_clip_data.active
    : true;

  // picture owner id
  const clipOwnerId =
    typeof currentStateData.new_clip_data !== "undefined" &&
      typeof currentStateData.new_clip_data.clip_owner_id !== "undefined"
      ? currentStateData.new_clip_data.clip_owner_id
      : "";
  const checkUserDetails = checkUserDetailsFromCookie();

  //check if file is selected or not

  if (!(clipFile instanceof File) && validateFor !== "update") {
    error = true;
    errorMessages = {
      ...errorMessages,
      clip_file: "Please select the clip file",
    };
  } else {
    // if (clipFile instanceof File) {
    //   //validate the file name
    //   const validateFileName = isValidFileName(clipFile.name);
    //   const checkFileType = clipFile.type;

    //   if (!validateFileName) {
    //     error = true;
    //     errorMessages = {
    //       ...errorMessages,
    //       clip_file: "Invalid file name. The file name should only contain a-z, A-Z, 0-9, -, or _",
    //     };
    //   } else {
    //     if (!platformConfig.valid_file_types_for_clips.includes(checkFileType)) {
    //       error = true;
    //       errorMessages = {
    //         ...errorMessages,
    //         clip_file: `Invalid file type. Allowed types are: ${platformConfig.valid_file_types_for_clips_withExtention.join(
    //           ", "
    //         )}.`,
    //       };
    //     }
    //   }
    // }
  }
  // check campaign
  if (campaign === "") {
    error = true;
    errorMessages = {
      ...errorMessages,
      clip_campaign: "Please select the campaign",
    };
  }
  // check start date
  if (clipStartDate === "") {
    error = true;
    errorMessages = {
      ...errorMessages,
      clip_start_date: "Please select the start date for the clip",
    };
  }
  // check end date
  if (clipEndDate === "") {
    error = true;
    errorMessages = {
      ...errorMessages,
      clip_end_date: "Please select the end date for the clip",
    };
  }
  // validate that start should not be after end date and vice versa
  if (clipStartDate !== "" && clipEndDate !== "") {
    const formattedStartDate = clipStartDate; // dateInCorrectTimezone();
    const formattedEndDate = clipEndDate; //dateInCorrectTimezone(clipEndDate);
    //start/end should not be after end date
    if (formattedEndDate < formattedStartDate) {
      error = true;
      errorMessages = {
        ...errorMessages,
        clip_end_date: "End date must be after the start date",
      };
    }
  }
  if (clipStartDate !== "" && clipEndDate !== "" && campaign !== "") {
    let clipStart = clipStartDate;
    // clipStart.setHours(0, 0, 0, 0);
    let clipEnd = clipEndDate;
    // clipEnd.setHours(0, 0, 0, 0);

    // start and end date should lie in the campaign's date range.
    // A missing bound must not be compared as false: any Date is greater than false,
    // which rejects a valid stop (end date moved to today) when campaign dates were not loaded.
    const rawCampaignStart = currentStateData.new_clip_data?.campaign_start_date;
    const rawCampaignEnd = currentStateData.new_clip_data?.campaign_end_date;
    const campaignStartDate = rawCampaignStart ? formatToStandardDate(rawCampaignStart) : null;
    const campaignEndDate = rawCampaignEnd ? formatToStandardDate(rawCampaignEnd) : null;
    const campaignStartValid = campaignStartDate instanceof Date && !isNaN(campaignStartDate.getTime());
    const campaignEndValid = campaignEndDate instanceof Date && !isNaN(campaignEndDate.getTime());

    if (campaignStartValid && clipStart < campaignStartDate) {
      error = true;
      errorMessages = {
        ...errorMessages,
        clip_start_date: "Clip start date should be within the campaign start and end date range.",
      };
    }

    if (campaignEndValid && clipStart > campaignEndDate) {
      error = true;
      errorMessages = {
        ...errorMessages,
        clip_end_date: "Clip end date should be within the campaign start and end date range.",
      };
    }

    if (campaignStartValid && clipEnd < campaignStartDate) {
      error = true;
      errorMessages = {
        ...errorMessages,
        clip_start_date: "Clip start date should be within the campaign start and end date range.",
      };
    }

    if (campaignEndValid && clipEnd > campaignEndDate) {
      error = true;
      errorMessages = {
        ...errorMessages,
        clip_end_date: "Clip end date should be within the campaign start and end date range.",
      };
    }
  }


  // weeks
  if (!clipOptimized) {
    // When not optimized, always use ALL available weeks
    clipWeeks = currentStateData.campaign_weeks?.map((week) => week.value).filter(v => v !== "all") || [];
  } else {
    // When optimized, weeks must be selected
    if (clipWeeks.length === 0) {
      error = true;
      errorMessages = {
        ...errorMessages,
        clip_weeks: "Please select the weeks",
      };
    }
  }

  // week days
  if (!clipOptimized) {
    // When not optimized, always use ALL available weekdays
    clipWeekDays = getWeekdaysInRange(
      currentStateData.new_clip_data?.clip_start_date,
      currentStateData.new_clip_data?.clip_end_date
    ).map((wd) => wd.label) || ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
  } else {
    // When optimized, weekdays must be selected
    if (clipWeekDays.length === 0) {
      error = true;
      errorMessages = {
        ...errorMessages,
        clip_week_days: "Please select the days",
      };
    }
  }

  // check slots (diese Logik kann bleiben wie sie ist)
  if (slot_oneStart === "" && slot_oneEnd === "") {
    error = true;
    errorMessages = {
      ...errorMessages,
      clip_time_slot: "Please select the time slot",
    };
  }

  // check hours of day
  if (!clipOptimized) {
    clipHoursOfDay = currentStateData.stores_available_hours;
    // fallback
    if (clipHoursOfDay.length === 0) {
      clipHoursOfDay = Array.from({ length: 24 }, (_, i) => i);
    }
  } else {
    // When optimized, hours must be selected and valid
    if (clipHoursOfDay.length === 0) {
      error = true;
      errorMessages.hours = "Please select at least one timeslot";
    }

    const invalidHours = clipHoursOfDay.filter(
      (h) => typeof h !== "number" || h < 0 || h > 23
    );

    if (invalidHours.length > 0) {
      error = true;
      errorMessages.hours = "Invalid hour selection";
    }
  }

  // check slots
  if (slot_oneStart === "" && slot_oneEnd === "") {
    error = true;
    errorMessages = {
      ...errorMessages,
      clip_time_slot: "Please select the time slot",
    };
  }

  // check contacts
  if (clipUnlimited === false && clipContacts === "") {
    error = true;
    errorMessages = {
      ...errorMessages,
      clip_contacts: "Please select the contacts",
    };
  }
  // check volume
  if (clipVolume === "") {
    error = true;
    errorMessages = {
      ...errorMessages,
      clip_volume: "Please select the volume",
    };
  }
  // check stores
  if (clipStores.length === 0) {
    error = true;
    errorMessages = {
      ...errorMessages,
      clip_stores: "Please select the stores",
    };
  }

  // retailer company
  if (ownerRequiredOnCreate(checkUserDetails.user_detail.user_type, validateFor, clipOwnerId) === true) {
    error = true;
    errorMessages = {
      ...errorMessages,
      clip_owner: "Please select the owner",
    };
  }

  // show error message (if error === true)
  if (error === true) {
    //Debuging log print Error
    //console.log(errorMessages)
    toast("Please fill in valid information", { type: "error" });
    return {
      error: true,
      errorMessages,
    };
  } else {
    const formData = new FormData();
    const storesArr = currentStateData.stores_list_for_visuals.filter((store) => {
      return clipStores.includes(store.name) ? store : false;
    });
    const retailerIds = currentStateData.stores_list_for_visuals
      .filter((store) => {
        return clipStores.includes(store.name);
      })
      .map((storeRecord) => {
        return storeRecord.retailer_id;
      });

    let weekNums =
      typeof currentStateData.campaign_weeks !== "undefined"
        ? clipWeeks.filter((clipWeekNum) => {
          const campaignWeekNumArr = currentStateData.campaign_weeks.map((campaignWeekArr) => {
            return campaignWeekArr.value;
          });

          if (campaignWeekNumArr.includes(clipWeekNum)) {
            return true;
          } else {
            return false;
          }
        })
        : clipWeeks;

    let dataObject = {
      id_campaign: parseInt(campaign),
      date_start: dbFormatDate(new Date(clipStartDate)),
      date_end: dbFormatDate(new Date(clipEndDate)),
      hours: clipHoursOfDay,
      weeks: weekNums.length !== 0 ? weekNums : currentStateData.campaign_weeks?.map((week) => week.value),
      weekdays: clipWeekDays.map((weekDay) => {
        return typeof weekDay === "number" ? weekDay : weekDayNum[weekDay];
      }),
      contacts: clipUnlimited === false ? parseInt(clipContacts) : 0,
      volume: parseInt(clipVolume),
      unlimited: clipUnlimited,
      stores: storesArr,
      retailer_ids: retailerIds,
      active: playoutStatus,
    };

    if (validateFor === "update") {
      dataObject = {
        ...dataObject,
        id: currentStateData.new_clip_data.id,
      };
    }

    if (shouldSendSelectedOwner(checkUserDetails.user_detail.user_type, validateFor, clipOwnerId) === true) {
      dataObject = {
        ...dataObject,
        id_company_owner: clipOwnerId,
      };
    }

    // Append file field
    if (clipFile instanceof File) {
      formData.append("file", clipFile);
      dataObject = {
        ...dataObject,
        content: clipFile.name,
        new_content: true,
      };
    } else {
      if (validateFor === "update") {
        dataObject = {
          ...dataObject,
          content: currentStateData.new_clip_data.content_name,
          new_content: false,
        };
      }
    }

    // Append non-file fields
    // formData.append("data", JSON.stringify(dataObject));

    // return validated request body for the clips add/update operation
    return {
      error: false,
      clips_file_request: clipFile instanceof File ? formData : "",
      clips_request: dataObject,
    };
  }
};

// to save clips information at backend
export const saveClipsInformation = async (usedfor, selectedFiles, showSuccessMessage) => {
  const currentStateData = store.getState().userSelections;
  const validateInfo = validateClipsInfo(usedfor, selectedFiles);
  let successFlag = false;
  let clipsResponse = {};
  if (validateInfo.error === true) {
    clipsResponse = {
      ...clipsResponse,
      success: false,
      errorMessages: validateInfo.errorMessages,
    };
  } else {
    const apiEndpoint = usedfor === "update" ? "clips/update" : "clips/add";
    const apiMethod = usedfor === "update" ? "PUT" : "POST";
    const userDetail = checkUserDetailsFromCookie();
    const rootCompanyId = userDetail.user_detail.root_company_id;
    const apiRequestParam = validateInfo.clips_request;
    // Recorded before the request. The clip form is cleared after a successful save.
    const requestOwnerId = apiRequestParam.id_company_owner;
    const ownerId =
      typeof requestOwnerId !== "undefined" && requestOwnerId !== ""
        ? requestOwnerId
        : ownerCompanyIdForSave(userDetail.user_detail, currentStateData.new_clip_data?.clip_owner_id);
    stageOwnerOutsideRetailerNotice(ownerId, usedfor !== "update");

    // we will upload the file first (if file request obj is returned from validate funtion)
    if (validateInfo.clips_file_request !== "") {
      // upload file to s3
      /*
        update: 28 April 2025,
        validateInfo.clips_request.retailer_ids replaced by rootCompanyId as asked by the backend.
      */
      let uploadApiEndpoint = "api/bucket/clip/upload?company_id=" + rootCompanyId;
      if (typeof apiRequestParam.id_company_owner !== "undefined" && apiRequestParam.id_company_owner !== "") {
        uploadApiEndpoint += "&owner_id=" + apiRequestParam.id_company_owner;
      }
      const saveClipFile = await sendApiRequestWithFile(uploadApiEndpoint, validateInfo.clips_file_request, "POST");
      // file upload successful, then save other clip information i.e., campaign, stores etc.
      if (typeof saveClipFile.success !== "undefined" && saveClipFile.success === true) {
        // send another request to save clips information, previous api call will only upload the clip
        const saveClipInfo = await sendApiRequest(apiEndpoint, apiRequestParam, apiMethod);
        // validate whether clips info api succeeded or failed
        if (typeof saveClipInfo.success !== "undefined" && saveClipInfo.success === true) {
          successFlag = true;
        } else {
          successFlag = false;
        }
      } else {
        successFlag = false;
      }
    } else {
      // send request to save clips information, file upload api is not needed in this case because file request obj is sent as empty from validate function
      const saveClipInfo = await sendApiRequest(apiEndpoint, apiRequestParam, apiMethod);
      // validate api succeeded or not
      if (typeof saveClipInfo.success !== "undefined" && saveClipInfo.success === true) {
        successFlag = true;
      } else {
        successFlag = false;
      }
    }

    if (successFlag === true) {
      const successMessage = usedfor === "update" ? "Visual updated successfully" : "Visual added successfully";
      clipsResponse = {
        ...clipsResponse,
        success: true,
        errorMessages: "",
      };
      // reset filters and load clips list again
      // store.dispatch(deleteItems(["clip_list_filters", "filtered_clips_list", "clips_list_filter_applied"]));
      // deleteCookie("clip_list_filters", true);
      // manageClipsPaginationCookie({ scroll_pos: 0 });
      // loadClips(0, currentStateData.clips_pagination.pageSize);
      setBatchLoadingStatus(true)
      loadClips(0, currentStateData.clips_pagination.pageSize).then(() =>
        setBatchLoadingStatus(false)
      );
      if (showSuccessMessage === true) {
        toast(successMessage, { type: "success" });
      }
      showStagedOwnerOutsideRetailerNotice();
    } else {
      clearStagedOwnerOutsideRetailerNotice();
      toast("Sorry, Something went wrong", { type: "error" });
      clipsResponse = {
        ...clipsResponse,
        success: false,
        errorMessages: "Sorry, Something went wrong",
      };
    }
  }

  return clipsResponse;
};

export const resetClipsData = () => {
  store.dispatch(
    deleteItems([
      "new_clip_data",
      "campaign_weeks",
      "clip_stores",
      "restructured_stores_for_visuals",
      "filtered_regions",
      "selected_region_ids",
      "selected_region_names",
      "selected_region_options",
      "selected_store_options",
      "campaign_stores_list",
      "stores_list_for_visuals",
      "store_pagination"
    ])
  );
};

// fetch s3 url for the clip file
export const fetchClipUrl = async (clipId, companyId) => {
  const sendRequest = await sendApiRequest("api/bucket/clip/generate-url/" + clipId + "/" + companyId, {}, "GET");
  if (typeof sendRequest.success !== "undefined" && sendRequest.success === true) {
    return {
      success: true,
      clip_file_url: sendRequest.response,
    };
  } else {
    return {
      success: false,
      message: "Sorry, something went wrong",
    };
  }
};

// load stores based on selected campaign (Add Clip). Kept separate from analytics_campaign_stores_list.
export const loadStoresFromCampaign = async (campaignId, campaignType) => {
  const checkStores = await sendApiRequest("stores/campaign?id_campaign=" + campaignId + "&type=" + campaignType, "", "GET");
  let response = {};
  if (checkStores.success === true) {
    saveStoresForVisuals(checkStores.response);
    response = {
      success: true,
    };
  } else {
    response = {
      success: false,
    };
  }
  return response;
};

/**
 * Load campaign stores for Campaign Analytics / Brand Report only.
 * Uses the same stores/campaign API, but writes a separate Redux key so we
 * never overwrite Add Clip's stores_list_for_visuals.
 */
export const loadAnalyticsCampaignStores = async (campaignId) => {
  const checkStores = await sendApiRequest(
    "stores/campaign?id_campaign=" + campaignId + "&type=contact",
    "",
    "GET"
  );

  if (checkStores.success === true) {
    const stores = Array.isArray(checkStores.response) ? checkStores.response : [];
    store.dispatch(
      updateStateData({
        analytics_campaign_stores_list: stores,
      })
    );
    return { success: true, stores };
  }

  store.dispatch(
    updateStateData({
      analytics_campaign_stores_list: [],
    })
  );
  return { success: false, stores: [] };
};

// check if listing check saved for a campaign
export const listingCheckSavedForCampaign = async (campaignId) => {
  // endpoint to send the request to
  const endpointUrl = "listingCheck/get/" + campaignId;
  // send the request
  const sendRequest = await sendApiRequest(endpointUrl, {}, "GET");
  if (typeof sendRequest.success !== "undefined" && sendRequest.success === true) {
    // listing check saved gtins and week period (to fetch the data again)
    const responseData = typeof sendRequest.response.listing_check_result !== "undefined" ? sendRequest.response.listing_check_result : sendRequest.response;
    if (typeof responseData === "object" && typeof responseData.stores !== "undefined") {
      store.dispatch(
        updateStateData({
          listing_check_saved_stores: responseData.stores,
          listing_check_saved_for_campaign: true,
        })
      );
    } else {
      store.dispatch(
        updateStateData({
          listing_check_saved_for_campaign: false,
        })
      );
    }
  } else {
    toast("Sorry, something went wrong", { type: "error" });
  }
}

const isNonemptyCompanyId = (companyId) =>
  companyId !== "" && companyId !== null && typeof companyId !== "undefined";

// Same owner concept as clip save (id_company_owner / clip_company_id), not login root_company_id.
export const resolveClipActiveListCompanyId = (explicitOwnerCompanyId) => {
  if (isNonemptyCompanyId(explicitOwnerCompanyId)) {
    return explicitOwnerCompanyId;
  }

  const currentStateData = store.getState().userSelections;
  const clipOwnerId = currentStateData?.new_clip_data?.clip_owner_id;
  if (isNonemptyCompanyId(clipOwnerId)) {
    return clipOwnerId;
  }

  const userCookie = checkUserDetailsFromCookie();
  const userDetail = userCookie?.user_detail;
  const fromSavePath = ownerCompanyIdForSave(userDetail, clipOwnerId);
  if (isNonemptyCompanyId(fromSavePath)) {
    return fromSavePath;
  }

  const hierarchyRootId = currentStateData?.company_hierarchy?.root?.id;
  if (isNonemptyCompanyId(hierarchyRootId)) {
    return hierarchyRootId;
  }

  const assignedCompanyId = userDetail?.assigned_company?.id;
  if (isNonemptyCompanyId(assignedCompanyId)) {
    return assignedCompanyId;
  }

  return undefined;
};

// load aktive stores for StoreHierachy Selection (because the active-list Endpoint chages a diffrent redux state)
export const loadActivStoresFromCampaign = async (campaignId, explicitOwnerCompanyId) => {
  let activeListEndpoint = "stores/active-list";
  const companyId = resolveClipActiveListCompanyId(explicitOwnerCompanyId);
  if (isNonemptyCompanyId(companyId)) {
    activeListEndpoint += "?company_id=" + encodeURIComponent(companyId);
  }

  const checkStores = await sendApiRequest(activeListEndpoint, "", "GET", { skipRetailerViewFilter: true });
  let response = {};
  if (checkStores.success === true) {
    // check if listing check saved for the campaign
    await listingCheckSavedForCampaign(campaignId);
    let listingCheckSavedStores = store.getState().userSelections.listing_check_saved_stores || [];
    // map through the stores and add listing_check_saved flag
    let storesList = checkStores.response.map((store) => {
      return listingCheckSavedStores.includes(store.id) ? { ...store, listing_check_saved: true } : { ...store, listing_check_saved: false };
    });
    saveStoresForVisuals(storesList);
    response = {
      success: true,
    };
  } else {
    response = {
      success: false,
    };
  }
  return response;
};